import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import * as echarts from 'echarts';
import { DashboardOperacaoResponse, DashboardOperacaoService } from 'src/services/dashboard-operacao.service';
import { fromEvent, of, Subscription, Subject, timer } from 'rxjs';
import { catchError, debounceTime, exhaustMap, takeUntil, tap } from 'rxjs/operators';
import { EstacaoGrupoService } from 'src/services/estacao-grupos.service';
import { PessoaService } from 'src/services/pessoa.service';
import { ColetaService } from 'src/services/coleta.service';
import { IntegracaoService } from 'src/services/integracao.service';

@Component({
    selector: 'app-dashboard-operacao',
    templateUrl: './dashboard-operacao.component.html',
    styleUrls: ['./dashboard-operacao.component.scss'],
    standalone: false
})
export class DashboardOperacaoComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('dashboardContainer', { static: true }) dashboardContainer!: ElementRef<HTMLElement>;
  @ViewChild('heatmapChart', { static: false }) heatmapChart!: ElementRef<HTMLDivElement>;
  @ViewChild('performanceChart', { static: false }) performanceChart!: ElementRef<HTMLDivElement>;
  @ViewChild('topEstacoesChart', { static: false }) topEstacoesChart!: ElementRef<HTMLDivElement>;

  loading = false;
  erro: string | null = null;
  dados: DashboardOperacaoResponse | null = null;

  ultimaAtualizacao: Date | null = null;
  proximaAtualizacaoSegundos = 60;
  fullscreenAtivo = false;

  dataSelecionada: Date = new Date();
  agrupamentoSelecionado: 'grupo' | 'cliente' | 'coletor' | 'integrador' = 'grupo';
  grupoSelecionado: number | null = null;
  clienteSelecionado: number | null = null;
  coletorSelecionado: number | null = null;
  integradorSelecionado: number | null = null;

  gruposDisponiveis: Array<{ label: string; value: number | null }> = [];
  clientesDisponiveis: Array<{ label: string; value: number | null }> = [];
  coletoresDisponiveis: Array<{ label: string; value: number | null }> = [];
  integradoresDisponiveis: Array<{ label: string; value: number | null }> = [];

  totais = {
    totalEstacoes: 0,
    ativos: 0,
    alertas: 0,
    offline: 0,
    percentualOk: 0
  };

  private refreshSub?: Subscription;
  private countdownSub?: Subscription;
  private resizeSub?: Subscription;
  private fullscreenSub?: Subscription;
  private destroy$ = new Subject<void>();
  private carregarDados$ = new Subject<void>();
  private heatmapInstance?: echarts.ECharts;
  private performanceInstance?: echarts.ECharts;
  private topEstacoesInstance?: echarts.ECharts;

  constructor(
    private dashboardService: DashboardOperacaoService,
    private estacaoGrupoService: EstacaoGrupoService,
    private pessoaService: PessoaService,
    private coletaService: ColetaService,
    private integracaoService: IntegracaoService
  ) {}

  ngOnInit(): void {
    this.carregarListas();
    this.iniciarStreamDados();
    this.iniciarAutoRefresh();
    this.carregarDados();
  }

  ngAfterViewInit(): void {
    this.resizeSub = fromEvent(window, 'resize')
      .pipe(debounceTime(200))
      .subscribe(() => this.redimensionarGraficos());

    this.fullscreenSub = fromEvent(document, 'fullscreenchange')
      .pipe(debounceTime(50))
      .subscribe(() => {
        this.fullscreenAtivo = !!document.fullscreenElement;
        this.redimensionarGraficos();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.refreshSub?.unsubscribe();
    this.countdownSub?.unsubscribe();
    this.resizeSub?.unsubscribe();
    this.fullscreenSub?.unsubscribe();
    this.heatmapInstance?.dispose();
    this.performanceInstance?.dispose();
    this.topEstacoesInstance?.dispose();
  }

  alternarFullscreen(): void {
    const elemento = this.dashboardContainer?.nativeElement;
    if (!elemento) {
      return;
    }

    if (!document.fullscreenElement) {
      elemento.requestFullscreen().then(() => {
        this.fullscreenAtivo = true;
        this.redimensionarGraficos();
      });
    } else {
      document.exitFullscreen().then(() => {
        this.fullscreenAtivo = false;
        this.redimensionarGraficos();
      });
    }
  }

  atualizarFiltros(): void {
    this.carregarDados();
  }

  selecionarAgrupamento(tipo: 'grupo' | 'cliente' | 'coletor' | 'integrador'): void {
    this.agrupamentoSelecionado = tipo;
    if (tipo !== 'grupo') {
      this.grupoSelecionado = null;
    }
    if (tipo !== 'cliente') {
      this.clienteSelecionado = null;
    }
    if (tipo !== 'coletor') {
      this.coletorSelecionado = null;
    }
    if (tipo !== 'integrador') {
      this.integradorSelecionado = null;
    }
    this.carregarDados();
  }

  private iniciarAutoRefresh(): void {
    // timer inicia em 60s (evita double-fetch com o carregarDados() do ngOnInit)
    this.refreshSub = timer(60000, 60000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.proximaAtualizacaoSegundos = 60;
        this.carregarDados();
      });

    this.countdownSub = timer(0, 1000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.proximaAtualizacaoSegundos > 0) {
          this.proximaAtualizacaoSegundos -= 1;
        }
      });
  }

  private iniciarStreamDados(): void {
    this.carregarDados$
      .pipe(
        tap(() => {
          this.loading = true;
          this.erro = null;
        }),
        exhaustMap(() => {
          const dataParam = this.formatarData(this.dataSelecionada);
          const agrupamentoId = this.obterAgrupamentoId();
          return this.dashboardService.carregarDashboard({
            data: dataParam,
            agrupamento: this.agrupamentoSelecionado,
            agrupamentoId
          }).pipe(
            catchError(() => {
              this.loading = false;
              this.erro = 'Não foi possível carregar o dashboard operacional. Tente novamente.';
              return of(null);
            })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((response) => {
        if (!response) {
          return;
        }
        this.dados = response;
        this.ultimaAtualizacao = response.ultima_atualizacao ? new Date(response.ultima_atualizacao) : null;
        this.atualizarTotais();
        this.renderizarGraficos();
        this.loading = false;
      });
  }

  private carregarDados(): void {
    this.carregarDados$.next();
  }

  private obterAgrupamentoId(): number | null {
    switch (this.agrupamentoSelecionado) {
      case 'cliente':
        return this.clienteSelecionado;
      case 'coletor':
        return this.coletorSelecionado;
      case 'integrador':
        return this.integradorSelecionado;
      default:
        return this.grupoSelecionado;
    }
  }

  private atualizarTotais(): void {
    if (!this.dados?.grupos?.length) {
      this.totais = { totalEstacoes: 0, ativos: 0, alertas: 0, offline: 0, percentualOk: 0 };
      return;
    }

    const totalEstacoes = this.dados.grupos.reduce((acc, grupo) => acc + grupo.total_estacoes, 0);
    const ativos = this.dados.grupos.reduce((acc, grupo) => acc + grupo.ativos, 0);
    const alertas = this.dados.grupos.reduce((acc, grupo) => acc + grupo.alertas, 0);
    const offline = this.dados.grupos.reduce((acc, grupo) => acc + grupo.offline, 0);
    const percentualOk = totalEstacoes > 0 ? (ativos / totalEstacoes) * 100 : 0;

    this.totais = {
      totalEstacoes,
      ativos,
      alertas,
      offline,
      percentualOk: Number(percentualOk.toFixed(2))
    };
  }

  private carregarListas(): void {
    this.estacaoGrupoService.index().subscribe((json) => {
      this.gruposDisponiveis = [
        { label: 'Todos os grupos', value: null },
        ...json.map(item => ({ label: item.nome, value: item.id }))
      ];
    });

    this.pessoaService.listEmpresas().subscribe((json) => {
      this.clientesDisponiveis = [
        { label: 'Todos os clientes', value: null },
        ...(json || [])
          .filter((item: any) => item.ativo === true)
          .map((item: any) => ({ label: item.nome, value: item.id }))
      ];
    });

    this.coletaService.index().subscribe((json) => {
      this.coletoresDisponiveis = [
        { label: 'Todos os coletores', value: null },
        ...json.map(item => ({ label: item.descricao, value: item.id }))
      ];
    });

    this.integracaoService.index().subscribe((json) => {
      this.integradoresDisponiveis = [
        { label: 'Todos os integradores', value: null },
        ...json.map(item => ({ label: item.descricao, value: item.id }))
      ];
    });
  }

  private renderizarGraficos(): void {
    if (!this.dados) {
      return;
    }

    requestAnimationFrame(() => {
      this.renderizarHeatmap();
      this.renderizarPerformance();
      this.renderizarTopEstacoes();
      this.redimensionarGraficos();
    });
  }

  private renderizarHeatmap(): void {
    if (!this.heatmapChart) {
      return;
    }

    if (this.heatmapInstance && this.heatmapInstance.getDom() !== this.heatmapChart.nativeElement) {
      this.heatmapInstance.dispose();
      this.heatmapInstance = undefined;
    }

    if (!this.heatmapInstance) {
      this.heatmapInstance = echarts.init(this.heatmapChart.nativeElement);
    }

    const grupos = this.dados?.heatmap?.grupos || [];
    const horas = Array.from({ length: 24 }, (_, i) => `${i}h`);
    const nomesGrupos = grupos.map(grupo => grupo.nome);
    const data: Array<[number, number, number]> = [];

    grupos.forEach((grupo, grupoIndex) => {
      grupo.valores.forEach(valor => {
        data.push([valor.hora, grupoIndex, valor.total]);
      });
    });

    this.heatmapInstance.setOption({
      tooltip: {
        position: 'top',
        formatter: (params: any) => {
          const [horaIndex, grupoIndex, total] = params.value as [number, number, number];
          return `${nomesGrupos[grupoIndex]} - ${horaIndex}h: ${total} msgs`;
        }
      },
      grid: {
        left: 80,
        right: 20,
        top: 20,
        bottom: 30
      },
      xAxis: {
        type: 'category',
        data: horas,
        axisLabel: { color: '#94a3b8' }
      },
      yAxis: {
        type: 'category',
        data: nomesGrupos,
        axisLabel: { color: '#94a3b8' }
      },
      visualMap: {
        min: 0,
        max: Math.max(...data.map(item => item[2]), 10),
        calculable: true,
        orient: 'horizontal',
        left: 'center',
        bottom: 0,
        inRange: {
          color: ['#0f172a', '#2563eb', '#22c55e', '#eab308', '#ef4444']
        }
      },
      series: [
        {
          name: 'Atividade',
          type: 'heatmap',
          data,
          label: { show: false },
          emphasis: {
            itemStyle: {
              shadowBlur: 8,
              shadowColor: 'rgba(0,0,0,0.35)'
            }
          }
        }
      ]
    });
  }

  private renderizarPerformance(): void {
    if (!this.performanceChart) {
      return;
    }

    if (this.performanceInstance && this.performanceInstance.getDom() !== this.performanceChart.nativeElement) {
      this.performanceInstance.dispose();
      this.performanceInstance = undefined;
    }

    if (!this.performanceInstance) {
      this.performanceInstance = echarts.init(this.performanceChart.nativeElement);
    }

    const grupos = this.dados?.heatmap?.grupos || [];
    const horas = Array.from({ length: 24 }, (_, i) => `${i}h`);

    const series = grupos.map(grupo => ({
      name: grupo.nome,
      type: 'line',
      smooth: true,
      data: horas.map((_, index) => {
        const valor = grupo.valores.find(item => item.hora === index);
        return valor ? valor.total : 0;
      }),
      symbol: 'circle',
      symbolSize: 6,
      lineStyle: { width: 3 }
    }));

    this.performanceInstance.setOption({
      tooltip: {
        trigger: 'axis'
      },
      legend: {
        top: 0,
        textStyle: { color: '#94a3b8' }
      },
      grid: {
        left: 60,
        right: 20,
        top: 40,
        bottom: 30
      },
      xAxis: {
        type: 'category',
        data: horas,
        boundaryGap: false,
        axisLabel: { color: '#94a3b8' }
      },
      yAxis: {
        type: 'value',
        axisLabel: { color: '#94a3b8' },
        splitLine: { lineStyle: { color: 'rgba(148,163,184,0.2)' } }
      },
      series
    });
  }

  private renderizarTopEstacoes(): void {
    if (!this.topEstacoesChart) {
      return;
    }

    if (this.topEstacoesInstance && this.topEstacoesInstance.getDom() !== this.topEstacoesChart.nativeElement) {
      this.topEstacoesInstance.dispose();
      this.topEstacoesInstance = undefined;
    }

    if (!this.topEstacoesInstance) {
      this.topEstacoesInstance = echarts.init(this.topEstacoesChart.nativeElement);
    }

    const topEstacoes = this.dados?.top_estacoes || [];
    const nomes = topEstacoes.map(estacao => estacao.nome);
    const valores = topEstacoes.map(estacao => estacao.total_mensagens);

    this.topEstacoesInstance.setOption({
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' }
      },
      grid: {
        left: 140,
        right: 20,
        top: 20,
        bottom: 20
      },
      xAxis: {
        type: 'value',
        axisLabel: { color: '#94a3b8' },
        splitLine: { lineStyle: { color: 'rgba(148,163,184,0.2)' } }
      },
      yAxis: {
        type: 'category',
        data: nomes,
        axisLabel: { color: '#94a3b8' }
      },
      series: [
        {
          type: 'bar',
          data: valores,
          itemStyle: {
            color: '#38bdf8',
            borderRadius: [6, 6, 6, 6]
          },
          label: {
            show: true,
            position: 'right',
            color: '#e2e8f0'
          }
        }
      ]
    });
  }

  private redimensionarGraficos(): void {
    this.heatmapInstance?.resize();
    this.performanceInstance?.resize();
    this.topEstacoesInstance?.resize();
  }

  formatarDataHora(data: Date | null): string {
    if (!data) {
      return '--';
    }
    return new Intl.DateTimeFormat('pt-BR', {
      dateStyle: 'short',
      timeStyle: 'medium'
    }).format(data);
  }

  formatarDataFiltro(data: Date): string {
    return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'full' }).format(data);
  }

  formatarStatus(tipo: string): string {
    const mapa: Record<string, string> = {
      estacao_offline: 'Estação offline',
      integracao_falha: 'Falha de integração'
    };

    return mapa[tipo] || 'Alerta operacional';
  }

  private formatarData(data: Date): string {
    const ano = data.getFullYear();
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const dia = String(data.getDate()).padStart(2, '0');
    return `${ano}-${mes}-${dia}`;
  }
}
